import { t } from "@lingui/macro"
import { Stack, Typography } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo } from "react"

import { DateTimePicker } from "src/components/DateTimePicker"
import { AccessGroup } from "src/components/ModalAccessGroup/AccessGroup"
import { SegmentPickerField } from "src/components/SegmentPickerField"
import { PollModalStore } from "src/modals/polls/store"
import {
    IQuestionsTabSectionsProps,
    PollSourceContext,
} from "src/modals/polls/types"
import { getPollMaxEndDate } from "src/shared/polls/utils/pollMaxEndDate"
import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"

export const PollSettingsSection = observer(
    ({ isViewMode }: IQuestionsTabSectionsProps) => {
        const store = useStore(PollModalStore)
        const globalStore = useStore(GlobalStore)

        useEffect(() => {
            // When component mounts, if accessGroupId is undefined, set it to the current global value
            if (store.form.get("accessGroupId") === undefined) {
                store.setAccessGroupId(globalStore.session.accessGroup.id)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [globalStore.session.accessGroup.id])

        const handleDateChange = useCallback(
            (date: Date | null) => {
                store.form.set("endDate", date)
            },
            [store.form],
        )

        const handleAccessGroupChange = useCallback(
            (id: number) => {
                store.form.set("accessGroupId", id)
            },
            [store.form],
        )

        const handleSegmentsChange = useCallback(
            (segments: number[]) => {
                store.form.set("segmentIds", segments)
            },
            [store.form],
        )

        const isDisabled = useMemo(
            () => store.isReadOnly || isViewMode,
            [store.isReadOnly, isViewMode],
        )

        const maxDate = useMemo(() => {
            return getPollMaxEndDate(store.publishPostAt)
        }, [store.publishPostAt])

        return (
            <Stack spacing={2}>
                <DateTimePicker
                    inputId="endDate"
                    label={t`poll.settings-section.end-date-label`}
                    value={store.form.get("endDate")}
                    onChangePicker={handleDateChange}
                    disabled={isDisabled}
                    minDate={store.publishPostAt ?? new Date()}
                    maxDate={maxDate}
                    error={Boolean(store.form.error("endDate"))}
                    helperText={store.form.error("endDate")}
                />
                <AccessGroup
                    accessGroupId={store.form.get("accessGroupId")}
                    errorMessage={store.form.error("accessGroupId") ?? ""}
                    onChange={handleAccessGroupChange}
                    disabled={isDisabled}
                />
                {store.sourceContext === PollSourceContext.PollList && (
                    <Stack spacing={1}>
                        <Typography variant="h6">{t`global.publish-to-label`}</Typography>
                        <SegmentPickerField
                            value={store.form.get("segmentIds") ?? []}
                            onChange={handleSegmentsChange}
                            accessGroupId={store.form.get("accessGroupId")}
                            disabled={isDisabled}
                        />
                    </Stack>
                )}
            </Stack>
        )
    },
)
